'use client'

import Link from 'next/link'
import { useState } from 'react'

interface SubMenuLink {
  label: string
  url: string
}

interface SubMenuItem {
  link: SubMenuLink
  subMenuItems?: SubMenuItem[]
}

interface SubMenuProps {
  items: (string | SubMenuItem)[]
  hideTimeout: any
  setHideTimeout: (timeout: any) => void
}

const SubMenu = ({ items, hideTimeout, setHideTimeout }: SubMenuProps) => {
  const [hoveredSubmenuItem, setHoveredSubmenuItem] = useState<string | null>(null)

  const handleSubmenuEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      setHideTimeout(null)
    }
  }

  const handleSubmenuLeave = () => {
    const timeout = setTimeout(() => {
      // setHoveredMenu(null)
      setHoveredSubmenuItem(null)
    }, 150)
    setHideTimeout(timeout)
  }

  return (
    <ul
      className="absolute translate-y-6 left-0 mt-2 bg-background border border-border text-foreground shadow-xl py-3 w-56 z-50 group-hover:transform group-hover:transition-all duration-200 group-hover:ease-in-out border-b-2 border-b-destructive rounded-md"
      onMouseEnter={handleSubmenuEnter}
      onMouseLeave={handleSubmenuLeave}
    >
      {items?.map((item: any, index) => (
        <li
          key={index}
          className="relative"
          onMouseEnter={() =>
            setHoveredSubmenuItem(typeof item === 'string' ? item : item?.link?.label)
          }
          onMouseLeave={() => {
            // Only clear submenu item if not moving to the submenu itself
            if (typeof item === 'string' || !item.subMenuItems) {
              setHoveredSubmenuItem(null)
            }
          }}
        >
          <Link
            href={typeof item === 'string' ? '#' : item?.link?.url || '#'}
            className="px-4 py-2.5 text-foreground group flex gap-2 items-center hover:bg-muted"
          >
            <span className="hidden group-hover:block group-hover:bg-destructive group-hover:h-2 group-hover:w-2 group-hover:rounded-full" />
            <span>{typeof item === 'string' ? item : item?.link?.label}</span>
            {/* Show arrow if item has submenu */}
            {typeof item === 'object' && !!item?.subMenuItems?.length && (
              <svg
                className="ml-auto w-4 h-4 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            )}
          </Link>

          {/* Nested submenu */}
          {typeof item === 'object' &&
            !!item?.subMenuItems?.length &&
            hoveredSubmenuItem === item?.link?.label && (
              <ul
                className="absolute bg-background border border-border left-full top-0 -ml-1 text-foreground shadow-xl py-3 w-48 z-60 transition-all duration-200 ease-in-out border-b-2 border-b-destructive rounded-md"
                onMouseEnter={() => {
                  handleSubmenuEnter()
                  setHoveredSubmenuItem(item?.link?.label)
                }}
                onMouseLeave={() => {
                  setHoveredSubmenuItem(null)
                  handleSubmenuLeave()
                }}
              >
                {item.subMenuItems.map((subItem: any, subIndex: any) => (
                  <li key={subIndex}>
                    <Link
                      href={subItem?.link?.url || '#'}
                      className=" px-4 py-2.5 text-white hover:bg-gray-700/50 group flex gap-2 items-center"
                    >
                      <span className="hidden group-hover:block group-hover:bg-destructive group-hover:h-2 group-hover:w-2 group-hover:rounded-full" />
                      {subItem?.link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
        </li>
      ))}
    </ul>
  )
}

export default SubMenu
